import { toParams } from 'src/app/core/utilities/utility-functions';
import { SnackbarService } from './../snackbar.service';
import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MessageErrorResolver } from "src/app/core/utilities/message-error-resolver";
import { LoadingService } from "../loading.service";
import { catchError, map, Observable } from "rxjs";
import { ResponseDto } from "@models/exceptions/response-dto";
import { IPage } from "@models/pagination/page";
import { PageRequest } from "@models/pagination/page-request";
import { ISimpleStepParameterizationDto } from "@models/competition/step-paramaterization/dtos/simple-step-parametrization-dto";
import { StepParametrizationFilter } from "@models/competition/step-paramaterization/filters/step-parametrization-filter";
import { environment } from "@env/environment";
import { ICompleteStepParameterizationDto } from "@models/competition/step-paramaterization/dtos/complete-simple-step-parameterization-dto";
import { ParameterizationPostForm } from "@models/competition/step-paramaterization/form/parameterization-post-form";
import { CompetitionModelFilter } from '@models/competition/step-paramaterization/filters/competition-model-filter';
import { ICompetitionModelDto } from '@models/competition/step-paramaterization/dtos/competition-model-dto';

type ResponseEdicaoEtapaBloqueadaDto = { petBlEdicaoBloqueada: boolean };

@Injectable({
  providedIn: 'root'
})
export class StepParameterizationService {

  private url: string = environment.apiUrl + '/v1/parametrizacao-etapa';
  private modelUrl: string = environment.apiUrl + '/v1/modelos-competicao';

  private snackbarService = inject(SnackbarService);
  private httpClient = inject(HttpClient);
  private messageErrorResolver = inject(MessageErrorResolver)
  private loadingService = inject(LoadingService)

  public getSimpleStepParametrization$(
    filters: StepParametrizationFilter,
    pageRequest: PageRequest
  ): Observable<IPage<ISimpleStepParameterizationDto>> {
    let params = toParams(filters, pageRequest);
    return this.httpClient
      .get<ResponseDto<IPage<ISimpleStepParameterizationDto>>>(this.url, { params })
      .pipe(
        map((response) => response.response),
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          throw err;
        })
      );
  }

  public getStepParametrizationById$(petNrId: number): Observable<ICompleteStepParameterizationDto> {
    return this.httpClient
      .get<ResponseDto<ICompleteStepParameterizationDto>>(`${this.url}/${petNrId}`)
      .pipe(
        map((response) => response.response),
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          throw err;
        })
      );
  }

  public createStepParametrization$(
    form: ParameterizationPostForm
  ): Observable<ICompleteStepParameterizationDto> {
    return this.httpClient
      .post<ResponseDto<ICompleteStepParameterizationDto>>(this.url, form)
      .pipe(
        map((response) => response.response),
      );
  }

  public updateTemplateId(petNrId: number, ettNrId: number): Observable<void> {
    const params = toParams({ ettNrId: ettNrId });
    return this.httpClient.patch<void>(`${this.url}/${petNrId}/template`, params).pipe(
      catchError((err) => {
        this.messageErrorResolver.resolveMessage(err);
        throw err;
      })
    );
  }

  public editStepParametrization$(
    form: ParameterizationPostForm
  ): Observable<ICompleteStepParameterizationDto> {
    return this.httpClient
      .put<ResponseDto<ICompleteStepParameterizationDto>>(this.url, form)
      .pipe(
        map((response) => response.response),
      );
  }

  public deleteStepParametrization(stepId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/${stepId}`).pipe(
      catchError((err) => {
        this.snackbarService.showInfo(err.response.message);
        throw err;
      })
    );
  }
  public blockStepParametrizationEdit(petNrId: number, value: boolean): Observable<ResponseDto<ResponseEdicaoEtapaBloqueadaDto>> {
    const params = toParams({ petBlEdicaoBloqueada: value });

    return this.httpClient.patch<ResponseDto<ResponseEdicaoEtapaBloqueadaDto>>(
      `${this.url}/${petNrId}/bloquear-edicao`, null, { params }
    );
  }

  public getCompetitionModels$(
    filters: CompetitionModelFilter,
    pageRequest: PageRequest): Observable<IPage<ICompetitionModelDto>> {
    let params = new HttpParams();

    if (pageRequest?.size != null) params = params.set('size', pageRequest.size);
    if (pageRequest?.page != null) params = params.set('page', pageRequest.page);

    if (filters?.mocNrId != null) params = params.set('mocNrId', filters.mocNrId);
    if (filters?.mocTxNome) params = params.set('mocTxNome', filters.mocTxNome);
    if (filters?.mocTxTipo) params = params.set('mocTxTipo', filters.mocTxTipo);
    if (filters?.fedNrId != null) params = params.set('fedNrId', filters.fedNrId);
    if (filters?.espNrId != null) params = params.set('espNrId', filters.espNrId);
    if (filters?.mocBlEmbateDireto != null) {
      params = params.set('mocBlEmbateDireto', String(filters.mocBlEmbateDireto));
    }
    if (filters?.mocBlConfrontosMesmaChave != null) {
      params = params.set('mocBlConfrontosMesmaChave', String(filters.mocBlConfrontosMesmaChave));
    }
    return this.httpClient
      .get<ResponseDto<IPage<ICompetitionModelDto>>>(this.modelUrl, { params })
      .pipe(map(res => res.response),
        catchError(err => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          throw err;
        })
      );
  }
}
